// The composition that happens INSIDE one call.
//
// Every instrument here measured between calls, and the retraction showed why that
// missed: deduped, the deepest dependency chain in the debugger corpus is three, yet
// a single call carries
//
//   .symfix D:\symbols; .reload /f ntdll.dll; .echo ===UMGL===; x ntdll!*Umgl*;
//   .echo ===LDR_ETW===; …
//
// and the result comes back as one long dump split by those markers. The agent wrote
// its own table of contents, because it knew the answer would be too long to read
// linearly and it needed to find each part again.
//
// THAT IS THE SIGNAL, and it is a round trip rather than a guess: a delimiter the
// COMMAND emitted, found again in the RESULT. No vocabulary, no language assumption,
// no threshold on how strange the text looks. An agent that sections its own output
// is telling you the output needs sectioning.
//
// Dual use, and the same computation serves both. Across a corpus it says which
// calls are compositions rather than single acts. Within one trace it is a reading:
// this call asked six questions, and here is each answer under the question it
// belongs to.

// A delimiter an agent writes to find its way back: runs of punctuation around a
// label. Deliberately requires the decoration — a bare `echo hello` is output, not
// a heading. The terminator set includes a QUOTE, because the last marker of a
// scripted call sits at the end of its -c argument and is closed by one — which is
// exactly where a "===DONE===" lives, the marker that says the script ran to the end.
// The LEADING set includes a quote for the same reason at the other end: the first
// marker of a scripted call sits immediately after the opening quote of its -c
// argument, and requiring whitespace before it dropped the opening section of every
// such call.
const EMIT_RE = /(?:^|[\s;&|("'])(?:\.echo|echo|print|printf|Write-Host|Write-Output)\s+(["']?)((?:[=#*~-]{2,}|\[)[^"'\n]{1,80}?)\1(?=\s*(?:$|[;&|)"'\n]))/g;

const clean = (s) => String(s || "").replace(/\s+/g, " ").trim();
/** The part of a marker that survives interpolation, as it will appear in output. */
const stable = (m) => clean(m).split(/[$`%]/)[0].trim();

/**
 * Delimiters this command emits into its own output stream.
 *
 * This is the canonical detector. `legibility.js` has its own narrower copy for the
 * linkage measure and the two had already drifted — that one requires whitespace
 * boundaries and so misses a marker closed by a quote, which is where a scripted
 * call's final "===DONE===" lives. It imports this one now.
 */
export function emitted(command) {
  const out = [];
  for (const m of String(command || "").matchAll(EMIT_RE)) {
    const s = stable(m[2]);
    if (s.length >= 3 && !out.includes(s)) out.push(s);
  }
  return out;
}

/**
 * Sections of a result, as the markers that came back and what sits under each.
 *
 * Only markers the command ITSELF emitted count. Matching any decorated line in the
 * output would count banners the tool prints on its own, which say nothing about
 * whether the agent needed the result carved up.
 */
export function sectionsOf(command, output) {
  const marks = emitted(command);
  const text = String(output || "");
  if (!marks.length || !text) return [];
  const found = [];
  for (const m of marks) {
    const at = text.indexOf(m);
    if (at !== -1) found.push({ marker: m, at });
  }
  found.sort((a, b) => a.at - b.at);
  return found.map((f, i) => {
    const end = i + 1 < found.length ? found[i + 1].at : text.length;
    const body = text.slice(f.at + f.marker.length, end);
    return {
      marker: f.marker,
      lines: body.split("\n").filter((l) => l.trim()).length,
      chars: body.length,
      // A section the agent asked for and that came back EMPTY is a finding: the
      // question was asked and the system had nothing to say.
      empty: !body.trim(),
    };
  });
}

/**
 * Per call: did the agent section its own output, and how far did that get.
 *
 *   asked      delimiters the command emitted
 *   answered   how many of them came back
 *   lost       emitted and never seen again — the call did not run that far, or the
 *              result was truncated before reaching it
 */
export function compositionOf(call) {
  const asked = emitted(call?.command);
  const sections = sectionsOf(call?.command, call?.output);
  return {
    asked: asked.length,
    answered: sections.length,
    lost: asked.length - sections.length,
    empty: sections.filter((s) => s.empty).length,
    sections,
    // One call carrying several delimited questions is a composition, whatever its
    // dependency chain looks like.
    composed: sections.length >= 2,
  };
}

/** Corpus-level: what share of calls compose several questions into one. */
export function compositionRate(calls) {
  const list = calls || [];
  if (!list.length) return { calls: 0, composed: 0, rate: 0, meanSections: 0, maxSections: 0, lost: 0 };
  let composed = 0;
  let sections = 0;
  let lost = 0;
  let max = 0;
  for (const c of list) {
    const k = compositionOf(c);
    if (k.composed) composed += 1;
    sections += k.answered;
    lost += k.lost;
    if (k.answered > max) max = k.answered;
  }
  return {
    calls: list.length,
    composed,
    rate: Math.round((composed / list.length) * 100) / 100,
    meanSections: Math.round((sections / list.length) * 100) / 100,
    maxSections: max,
    lost,
  };
}
